import { useTimeout } from '@eturi/react'
import { Trans, useT } from '@eturi/react-i18next'
import type { SSOLoginScheme } from '@op/services'
import { isAuthenticated$ } from '@op/services'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Redirect, useHistory } from 'react-router-dom'
import { redirect$ } from '../../compound-selectors/app-misc'
import { Btn, PageLoadingSpinner } from '../../widgets'
import {
	getConnectDeepLinkUrl,
	getSSOInitURL,
	GOOGLE_POPUP_NAME,
	SSOInitUrlState,
} from '../SSO/sso-clients'
import { useLandingSSOHandler } from '../SSO/useLandingSSOHandler'
import { LandingWrap } from './LandingWrap'

export const createSSOLandingPages = (type: SSOLoginScheme) => {
	/**
	 * An "init" page for SSO simply does a redirect to the correct SSO endpoint for external
	 * clients. This is required because OAuth providers require clients come from a particular
	 * origin, and are blocked otherwise.
	 */
	const SSOInitPage = () => {
		useEffect(() => {
			location.replace(getSSOInitURL(type, SSOInitUrlState.CONNECT))
		}, [])

		return <PageLoadingSpinner />
	}

	SSOInitPage.displayName = `SSOInitPage(${type})`

	/**
	 * An SSO "redirect" page is a page that handles the redirect after the OAuth provider
	 * authenticates. It will parse parameters from the redirect, and handle them based on the
	 * `state` (if any) that was passed through the provider. `state` is defined based on how the
	 * OAuth URL is constructed, and is passed through by the provider when finished. We have states
	 * to handle OurPact Connect, as well as Google popup flow. Without a state, default redirect
	 * actions are invoked using `useSSOHandler`.
	 */
	const SSORedirectPage = () => {
		const [t] = useT('sso.connect')
		const history = useHistory()
		const isAuthenticated = useSelector(isAuthenticated$)
		const redirect = useSelector(redirect$)
		const [didParseURLParams, setUrlParamsParsed] = useState(false)
		const [connectURL, setConnectURL] = useState<Maybe<string>>(null)
		const handleSSO = useLandingSSOHandler()
		const [setUrlParamsParsedTimeout] = useTimeout()

		const securePostMessage = (code: string | null) => {
			if (!code) return window.close()

			try {
				const opener: Maybe<WindowProxy> = window.opener
				const origin = window.location.origin

				/**
				 * NOTE: Important security checks here:
				 *  1. The window must be a popup window named `GOOGLE_POPUP_NAME`
				 *  2. There must be an "opener"
				 *  3. The opener's origin must be the same as our origin.
				 *  ----
				 *  These steps ensure we only post a message to ourselves.
				 */
				if (window.name === GOOGLE_POPUP_NAME && opener && opener.location.origin === origin) {
					opener.postMessage({ code }, origin)
				} else {
					window.close()
				}
			} catch {
				window.close()
			}
		}

		useEffect(() => {
			const params = new URLSearchParams(location.search)
			const [authCode, ssoRedirectState] = [params.get('code'), params.get('state')]

			// We give the `postMessage` a few ms to resolve. The parent should close this window.
			setUrlParamsParsedTimeout(() => {
				setUrlParamsParsed(true)
			}, 100)

			// Connect is handled by initiating a deep link. We set the connect URL as a backup in
			// case the deep link doesn't work. Return early and let the spinner show.
			if (ssoRedirectState == SSOInitUrlState.CONNECT && authCode) {
				const url = getConnectDeepLinkUrl(type, authCode)
				setConnectURL(url.toString())
				window.open(url)
				return
			}

			// For Google popup, we post a secure message of the parsed args and return
			if (ssoRedirectState == SSOInitUrlState.GOOGLE_POPUP && type == 'sso_google') {
				return securePostMessage(authCode)
			}

			if (!authCode) return history.replace('/')

			handleSSO(authCode, type)
		}, [])

		/**
		 * We need to wait until the URLParams are parsed before redirecting. Otherwise, when the
		 * Google popup redirects back for SSO verification when logged in (account deletion, Vew
		 * password reset), the redirect will clear the params before they can be parsed, and the
		 * window will hang open.
		 */
		if (didParseURLParams && isAuthenticated) return <Redirect to={redirect} />

		return (
			<LandingWrap noFeatures>
				<PageLoadingSpinner className="!mt-2" />
				{connectURL && (
					<div className="text-center text-balance">
						<h3 className="mb-3">{t('title')}</h3>

						<p className="mb-8">
							<Trans i18nKey="desc" t={t} />
						</p>

						<Btn className="mx-6 mb-3" tag="a" href={connectURL}>
							{t('open')}
						</Btn>
					</div>
				)}
			</LandingWrap>
		)
	}

	SSORedirectPage.displayName = `SSORedirectPage(${type})`

	return [SSOInitPage, SSORedirectPage] as const
}
